import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  MessageSquare, Building2, Settings, LogOut, Menu, X, FileText,
} from "lucide-react";

const NAV = [
  { label: "Mira", icon: MessageSquare, path: "/coach" },
  { label: "My School", icon: Building2, path: "/school" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

function PlanLink({ token, onClick }) {
  return (
    <a
      href={`/plan/${token}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid="nav-my-growth-plan"
      className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent transition-colors"
    >
      <FileText className="h-[18px] w-[18px]" />
      My Growth Plan
    </a>
  );
}

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          isActive
            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20"
            : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px]" />
      {item.label}
    </NavLink>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [planToken, setPlanToken] = useState(null);
  const isChat = location.pathname === "/coach";

  useEffect(() => {
    if (!user) return;
    api.get("/me/plan").then(({ data }) => setPlanToken(data?.token || null)).catch(() => {});
  }, [user]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background mm-grid-bg">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-slate-800 bg-slate-950/70 backdrop-blur-xl z-30">
        <nav className="flex-1 px-3 pt-6 space-y-1 mm-scroll overflow-y-auto">
          {NAV.map((i) => <NavItem key={i.path} item={i} />)}
          {planToken && <PlanLink token={planToken} />}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="px-3.5 py-2 text-xs text-slate-500 truncate font-mono">{user?.email}</div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
          >
            <LogOut className="h-[18px] w-[18px]" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-end px-4 h-14 border-b border-slate-800 bg-slate-950/90 backdrop-blur-xl">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="p-2 text-slate-300">
          <Menu className="h-6 w-6" />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative ml-auto w-72 bg-slate-950 border-l border-slate-800 p-4 mm-fade-in">
            <div className="flex items-center justify-end mb-4">
              <button onClick={() => setOpen(false)} className="p-2 text-slate-300"><X className="h-6 w-6" /></button>
            </div>
            <nav className="space-y-1">
              {NAV.map((i) => <NavItem key={i.path} item={i} onClick={() => setOpen(false)} />)}
              {planToken && <PlanLink token={planToken} onClick={() => setOpen(false)} />}
              <button onClick={doLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-rose-300 hover:bg-rose-500/10">
                <LogOut className="h-[18px] w-[18px]" /> Log out
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Main */}
      <main className={`lg:pl-64 ${isChat ? "" : "pb-10 lg:pb-0"}`}>
        {isChat ? (
          <div className="h-[calc(100dvh-3.5rem)] lg:h-[100dvh] px-3 pt-3 pb-2 lg:px-8 lg:py-6">{children}</div>
        ) : (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
            {user?.needs_password && (
              <div data-testid="set-password-prompt" className="mb-5 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm text-slate-200">Set a password so you can log back into your account anytime.</span>
                <button onClick={() => navigate("/settings")} data-testid="set-password-cta" className="text-sm font-semibold text-emerald-300 hover:text-emerald-200 underline">
                  Set password
                </button>
              </div>
            )}
            {children}
          </div>
        )}
      </main>
    </div>
  );
}
